import { Box, Button, Flex, Icon, Skeleton, Stack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DtType,
  PerspectiveBaseInfoFragment,
  ProjectType,
  Subtopic,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { ProjectPlot } from './ProjectPlot';
import { ModelCardData } from './ProjectView';
import { SubtopicDemographicInsights } from './SubtopicDemographicInsights';

// Lazy-loaded plot wrapper that only renders when visible
const LazyPlot = ({
  children,
  height,
  onVisible,
}: {
  children: React.ReactNode;
  height: string;
  onVisible?: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          onVisible?.();
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading 200px before visible
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [onVisible]);

  // Only apply minHeight when showing skeleton placeholder
  // Let loaded content determine its own height
  return (
    // The parent Stack uses alignItems="center", which makes flex children shrink to content width;
    // give the wrapper a definite width (full on mobile, 70% of the column on desktop) so the plot
    // fills it instead of collapsing to min-content (which rendered skinny).
    <Box ref={ref} width={{ base: '100%', md: '70%' }}>
      {isVisible ? (
        children
      ) : (
        <Skeleton height={height} width="100%" maxW="700px" borderRadius="6px" />
      )}
    </Box>
  );
};

interface ProjectTabViewOverallProps {
  allTwins: { name: string; id: string }[];
  selectedTwins: Set<{ name: string; id: string }>;
  setSelectedTwins: React.Dispatch<
    React.SetStateAction<Set<{ id: string; name: string }>>
  >;
  setDigitalTwinId: (id: string | undefined) => void;
  digitalTwinId?: string;
  topic: string;
  perspectives: PerspectiveBaseInfoFragment[];
  selectedDemographics?: Set<{ key: string; value: string }>;
  modelCardData?: ModelCardData;
  subtopics: Subtopic[];
  setPlotRef: (id: string, element: HTMLDivElement | null) => void;
  digitalTwinType: DtType;
  showMovement?: boolean;
  comparisonPerspectives?: PerspectiveBaseInfoFragment[];
  timelineProjects?: Array<{ id: string; dateCreated: string }>;
  currentProjectId?: string;
  comparisonProjectId?: string;
  allTimelinePerspectives?: Map<string, PerspectiveBaseInfoFragment[]>;
  projectType: ProjectType;
  requestTimelineData?: () => void;
  initialTabIndex?: number;
  // Change detection settings
  changeDetectionEnabled?: boolean;
  sensitivityStdDevs?: number;
  burnInPeriod?: number;
}

export const ProjectTabViewOverall = ({
  allTwins,
  selectedTwins,
  setSelectedTwins,
  setDigitalTwinId,
  digitalTwinId,
  topic,
  perspectives,
  selectedDemographics,
  modelCardData,
  subtopics,
  setPlotRef,
  digitalTwinType,
  showMovement,
  comparisonPerspectives,
  timelineProjects,
  currentProjectId,
  comparisonProjectId,
  allTimelinePerspectives,
  projectType,
  requestTimelineData,
  initialTabIndex,
  changeDetectionEnabled,
  sensitivityStdDevs,
  burnInPeriod,
}: ProjectTabViewOverallProps) => {
  // Track active tab for each plot (key: plot id, value: tab index)
  const [activeTabs, setActiveTabs] = useState<Record<string, number>>({});

  // Normalize overall perspectives
  const overallPerspectives = useMemo(() => {
    return perspectives.filter((perspective) => perspective.topic === 'topic');
  }, [perspectives]);

  const overallComparisonPerspectives = useMemo(() => {
    return (
      comparisonPerspectives?.filter(
        (perspective) => perspective.topic === 'topic'
      ) ?? []
    );
  }, [comparisonPerspectives]);

  const subtopicPerspectives = useMemo(() => {
    const bySubtopic = new Map<string, PerspectiveBaseInfoFragment[]>();

    // Group perspectives by subtopic
    perspectives
      .filter((perspective) => perspective.topic !== 'topic')
      .forEach((perspective) => {
        if (!bySubtopic.has(perspective.topic)) {
          bySubtopic.set(perspective.topic, []);
        }
        bySubtopic.get(perspective.topic)?.push(perspective);
      });

    return bySubtopic;
  }, [perspectives]);

  const subtopicComparisonPerspectives = useMemo(() => {
    const bySubtopic = new Map<string, PerspectiveBaseInfoFragment[]>();

    // Group comparison perspectives by subtopic
    (comparisonPerspectives ?? [])
      .filter((perspective) => perspective.topic !== 'topic')
      .forEach((perspective) => {
        if (!bySubtopic.has(perspective.topic)) {
          bySubtopic.set(perspective.topic, []);
        }
        bySubtopic.get(perspective.topic)?.push(perspective);
      });

    return bySubtopic;
  }, [comparisonPerspectives]);

  // Single-entity projects don't use anchor-based positioning, so suppress spectrum labels
  const isSingleEntity = allTwins.length <= 1;

  // Set digital twin ID on selection change
  useEffect(() => {
    if (selectedTwins.size === 1) {
      setDigitalTwinId(Array.from(selectedTwins)[0].id);
    }
  }, [selectedTwins, setDigitalTwinId]);

  // Render first plot immediately, lazy load the rest
  const renderPlot = (subtopic: Subtopic, idx: number) => {
    const matchingPerspectives = subtopicPerspectives.get(subtopic.name) || [];

    const plotContent = (
      <Flex
        height="fit-content"
        width="full"
        alignItems="center"
        ref={(el) => setPlotRef(`subtopic-${subtopic.name}`, el)}
        flexDirection="column"
        bg="white"
        p={2}
        borderRadius="6px"
        borderWidth="1px"
        borderColor={HelivanColor.border.primary}
        gap={2}
      >
        <Flex width="full" justifyContent="center">
          <ProjectPlot
            key={idx}
            type="subtopic"
            perspectives={matchingPerspectives}
            topic={subtopic.name}
            setDigitalTwinId={setDigitalTwinId}
            leftTitle={isSingleEntity ? undefined : (subtopic.leftTitle || undefined)}
            rightTitle={isSingleEntity ? undefined : (subtopic.rightTitle || undefined)}
            selectedTwins={selectedTwins}
            setSelectedTwins={setSelectedTwins}
            selectedDemographics={selectedDemographics}
            showMovement={showMovement}
            comparisonPerspectives={
              subtopicComparisonPerspectives.get(subtopic.name) ?? []
            }
            timelineProjects={timelineProjects}
            currentProjectId={currentProjectId}
            comparisonProjectId={comparisonProjectId}
            allTimelinePerspectives={allTimelinePerspectives}
            onTabChange={(tabIndex) => {
              setActiveTabs((prev) => ({
                ...prev,
                [`subtopic-${subtopic.name}`]: tabIndex,
              }));
            }}
            projectType={projectType}
            requestTimelineData={requestTimelineData}
            initialTabIndex={initialTabIndex}
            changeDetectionEnabled={changeDetectionEnabled}
            sensitivityStdDevs={sensitivityStdDevs}
            burnInPeriod={burnInPeriod}
          />
        </Flex>
        {(activeTabs[`subtopic-${subtopic.name}`] ?? 0) === 0 &&
          projectType === ProjectType.Explorable && (
            <SubtopicDemographicInsights
              perspectives={matchingPerspectives}
              leftTitle={isSingleEntity ? undefined : (subtopic.leftTitle || undefined)}
              rightTitle={isSingleEntity ? undefined : (subtopic.rightTitle || undefined)}
              digitalTwinType={digitalTwinType}
            />
          )}
      </Flex>
    );

    // First 2 plots render immediately, rest are lazy loaded. Responsive width (full on mobile, 70%
    // on desktop) so the wrapper takes a definite width inside the alignItems="center" Stack
    // (without it the plot collapses to min-content and renders skinny).
    if (idx < 2) {
      return <Box key={idx} width={{ base: '100%', md: '70%' }}>{plotContent}</Box>;
    }

    return (
      <LazyPlot key={idx} height="280px">
        {plotContent}
      </LazyPlot>
    );
  };

  if (perspectives.length === 0) {
    const isFirstInTimeline = !timelineProjects || timelineProjects.length <= 1;
    return (
      <Flex
        width="full"
        justifyContent="center"
        alignItems="center"
        py={16}
      >
        <VStack
          spacing={4}
          p={10}
          bg="white"
          borderRadius="12px"
          borderWidth="1px"
          borderColor={HelivanColor.border.primary}
          maxWidth="520px"
          textAlign="center"
        >
          <Text fontSize="lg" fontWeight="600" color={HelivanColor.text.black}>
            {isFirstInTimeline
              ? 'Not enough data to visualize yet'
              : 'No perspectives generated for this snapshot'}
          </Text>
          <Text fontSize="sm" color={HelivanColor.text.gray}>
            {isFirstInTimeline
              ? 'This is the first snapshot for this agent. To see perspective visualizations, click "Update Now" to create a new snapshot, or add more agents to the project.'
              : 'This snapshot did not produce any perspective data. Try clicking "Update Now" to generate a new snapshot, or navigate to a previous version using the timeline above.'}
          </Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Stack spacing={10} width="full" alignItems="center" pb={10}>
      {subtopics.map((subtopic, idx) => renderPlot(subtopic, idx))}

      {/* Overall 2D plot - hidden for single-entity projects (meaningless with 1 dot) */}
      {!isSingleEntity && <LazyPlot height="500px">
        <Flex
          height="fit-content"
          width="full"
          alignItems="center"
          flexDirection="column"
          bg="white"
          pt={2}
          borderRadius="6px"
          borderWidth="1px"
          borderColor={HelivanColor.border.primary}
          gap={2}
          ref={(el) => setPlotRef('overall', el)}
        >
          <ProjectPlot
            type="topic"
            perspectives={overallPerspectives}
            topic="Overall"
            setDigitalTwinId={setDigitalTwinId}
            selectedTwins={selectedTwins}
            setSelectedTwins={setSelectedTwins}
            selectedDemographics={selectedDemographics}
            showMovement={showMovement}
            comparisonPerspectives={overallComparisonPerspectives}
            timelineProjects={timelineProjects}
            currentProjectId={currentProjectId}
            comparisonProjectId={comparisonProjectId}
            allTimelinePerspectives={allTimelinePerspectives}
            onTabChange={(tabIndex) => {
              setActiveTabs((prev) => ({ ...prev, overall: tabIndex }));
            }}
            projectType={projectType}
            requestTimelineData={requestTimelineData}
            initialTabIndex={initialTabIndex}
            changeDetectionEnabled={changeDetectionEnabled}
            sensitivityStdDevs={sensitivityStdDevs}
            burnInPeriod={burnInPeriod}
          />
        </Flex>
      </LazyPlot>}

      <HelivanText variant="tinyBody" colorOverride="transparent">
        space
      </HelivanText>
    </Stack>
  );
};
